/**
 * OrderChat widget — realtime order chat for the storefront.
 *
 * Renders the merged message history (REST + live WS overlay), a composer, the
 * "Позвать продавца" action and the seller-SLA hint ("отвечает в течение 10 минут").
 * All transport/de-dup logic lives in {@link useOrderChat}; this is presentation +
 * input handling only.
 */
import {
  CALL_SELLER_TEXT,
  SELLER_SLA_NOTE,
  useOrderChat,
} from "@features/order-chat";
import { isImageAttachment } from "@entities/order";
import { ApiError } from "@shared/api";
import { useEffect, useRef, useState, type ReactNode } from "react";

import styles from "./OrderChat.module.css";

/** Max attachment size accepted client-side (server also enforces its own limit). */
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const ACCEPT_ATTACHMENT = "image/*,application/pdf";

export interface OrderChatProps {
  readonly orderId: number;
  /** The current user's backend id, to align own messages to the right. */
  readonly currentUserId: number | null;
  /**
   * Server-computed: whether the chat accepts new messages now (``Order.chat_postable``).
   * ``false`` locks the composer — true for in-flight orders and for a completed order while a
   * purchase receipt is requested, false otherwise.
   */
  readonly chatPostable: boolean;
  /** Optional node rendered as the first (pinned, system) entry in the log. */
  readonly summary?: ReactNode;
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { readonly detail?: unknown } | null;
    if (
      payload !== null &&
      typeof payload === "object" &&
      typeof payload.detail === "string"
    ) {
      return payload.detail;
    }
  }
  return "Не удалось отправить сообщение. Попробуйте ещё раз.";
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function OrderChat({
  orderId,
  currentUserId,
  chatPostable,
  summary,
}: OrderChatProps): JSX.Element {
  const chat = useOrderChat(orderId);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const locked = !chatPostable;

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [chat.messages.length]);

  const send = (text: string): void => {
    setError(null);
    chat.send(text).catch((err: unknown) => {
      setError(errorText(err));
    });
  };

  const onSubmit = (): void => {
    const text = draft.trim();
    if (text.length === 0 || locked) {
      return;
    }
    setDraft("");
    send(text);
  };

  const onPickFile = (): void => {
    if (locked) {
      return;
    }
    fileRef.current?.click();
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) {
      return;
    }
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      setError("Можно прикрепить только изображение или PDF.");
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError("Файл слишком большой (максимум 20 МБ).");
      return;
    }
    setError(null);
    chat.sendAttachment(file, draft.trim()).then(
      () => {
        setDraft("");
      },
      (err: unknown) => {
        setError(errorText(err));
      },
    );
  };

  const connection: { readonly label: string; readonly tone: string } =
    chat.connectionStatus === "open"
      ? { label: "онлайн", tone: "online" }
      : chat.connectionStatus === "connecting"
        ? { label: "подключение…", tone: "connecting" }
        : { label: "не в сети", tone: "offline" };

  return (
    <section className={styles.chat} aria-label="Чат заказа">
      <header className={styles.chatHead}>
        <span className={styles.chatTitle}>Чат с продавцом</span>
        <span
          className={styles.status}
          data-tone={connection.tone}
          aria-live="polite"
        >
          <span className={styles.statusDot} aria-hidden="true" />
          {connection.label}
        </span>
      </header>

      <div className={styles.log} role="log" aria-live="polite">
        {summary}
        <div className={styles.systemNote}>{SELLER_SLA_NOTE}</div>

        {chat.isLoading ? (
          <p className={styles.muted}>Загрузка…</p>
        ) : chat.messages.length === 0 ? (
          <p className={styles.muted}>Сообщений пока нет. Напишите продавцу.</p>
        ) : (
          chat.messages.map((message) => {
            const own =
              currentUserId !== null && message.sender === currentUserId;
            const time = formatTime(message.created_at);
            if (own) {
              return (
                <div key={message.id} className={styles.rowOwn}>
                  <div className={styles.bubbleWrapOwn}>
                    <div className={styles.bubbleOwn}>
                      {message.text ? (
                        <span className={styles.bubbleText}>{message.text}</span>
                      ) : null}
                      {message.attachment_url ? (
                        <Attachment url={message.attachment_url} own />
                      ) : null}
                    </div>
                    <span className={styles.caption}>Вы · {time}</span>
                  </div>
                </div>
              );
            }
            return (
              <div key={message.id} className={styles.rowSeller}>
                <span className={styles.avatar} aria-hidden="true">
                  M
                </span>
                <div className={styles.bubbleWrap}>
                  <div className={styles.bubbleSeller}>
                    {message.text ? (
                      <span className={styles.bubbleText}>{message.text}</span>
                    ) : null}
                    {message.attachment_url ? (
                      <Attachment url={message.attachment_url} />
                    ) : null}
                  </div>
                  <span className={styles.caption}>Продавец · {time}</span>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className={styles.panel}>
        {locked ? (
          <p className={styles.lockedNote} role="status">
            Заказ закрыт — чат недоступен.
          </p>
        ) : null}

        {error !== null ? (
          <p className={styles.errorNote} role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          className={styles.callSeller}
          disabled={chat.isSending || locked}
          onClick={() => send(CALL_SELLER_TEXT)}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M13 2 3 14h7l-1 8 10-12h-7z" />
          </svg>
          Позвать продавца
        </button>

        <div className={styles.composer}>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT_ATTACHMENT}
            className={styles.fileInput}
            onChange={onFileChange}
            tabIndex={-1}
            aria-hidden="true"
          />
          <button
            type="button"
            className={styles.clip}
            aria-label="Прикрепить файл"
            disabled={chat.isSending || locked}
            onClick={onPickFile}
          >
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m21 8-9.5 9.5a3.5 3.5 0 0 1-5-5L15 4a2.3 2.3 0 0 1 3.3 3.3L9 16.5" />
            </svg>
          </button>
          <input
            className={styles.input}
            type="text"
            placeholder={locked ? "Чат закрыт" : "Сообщение продавцу…"}
            value={draft}
            maxLength={4000}
            disabled={locked}
            aria-label="Сообщение продавцу"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
          />
          <button
            type="button"
            className={styles.send}
            aria-label="Отправить"
            disabled={chat.isSending || locked || draft.trim().length === 0}
            onClick={onSubmit}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M22 2 11 13" />
              <path d="m22 2-7 20-4-9-9-4z" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}

interface AttachmentProps {
  readonly url: string;
  readonly own?: boolean;
}

function Attachment({ url, own = false }: AttachmentProps): JSX.Element {
  if (isImageAttachment(url)) {
    return (
      <a
        className={styles.attachmentImageLink}
        href={url}
        target="_blank"
        rel="noreferrer"
      >
        <img
          className={styles.attachmentImage}
          src={url}
          alt="Вложение"
          loading="lazy"
        />
      </a>
    );
  }
  return (
    <a
      className={own ? styles.attachmentOwn : styles.attachment}
      href={url}
      target="_blank"
      rel="noreferrer"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M9 13h6" />
        <path d="M9 17h6" />
      </svg>
      <span>Документ</span>
    </a>
  );
}
